
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { ScrollArea } from "@shared/ui/scroll-area";
import { Badge } from "@shared/ui/badge";

export interface AuditLogEntry {
  id: string;
  action: "Created" | "Updated" | "Deleted";
  idName: string;
  entity: string;
  modifiedBy: string;
  date: string;
  time: string;
  details: string;
  format?: string;
  changes?: {
    field: string;
    oldValue: string;
    newValue: string;
  }[];
}

interface AuditLogDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  logEntry: AuditLogEntry | null;
}

const AuditLogDetails = ({ isOpen, onClose, logEntry }: AuditLogDetailsProps) => {
  if (!logEntry) return null;

  const getActionColor = (action: string) => {
    switch (action) {
      case "Created":
        return "bg-green-100 text-green-800";
      case "Updated":
        return "bg-blue-100 text-blue-800";
      case "Deleted":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[750px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Log Details
            <Badge className={getActionColor(logEntry.action)}>
              {logEntry.action}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID Configuration</p>
                <p className="text-base font-semibold">{logEntry.idName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Entity</p>
                <p className="text-base font-semibold">{logEntry.entity}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Modified By</p>
                <p className="text-base">{logEntry.modifiedBy}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Date & Time</p>
                <p className="text-base">{logEntry.date} at {logEntry.time}</p>
              </div>
            </div>
            
            {logEntry.format && (
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-2">Format</p>
                <div className="bg-gray-100 p-3 rounded-md">
                  <code className="text-base font-mono">{logEntry.format}</code>
                </div>
              </div>
            )}
            
            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-2">Description</p>
              <p className="text-base">{logEntry.details}</p>
            </div>
            
            {logEntry.changes && logEntry.changes.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-2">Changes</p>
                <div className="space-y-3">
                  {logEntry.changes.map((change, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-md">
                      <p className="font-medium text-sm">{change.field}</p>
                      <div className="grid grid-cols-2 gap-2 mt-1 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">From:</p>
                          <p className="font-mono bg-gray-100 p-1 rounded">{change.oldValue}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">To:</p>
                          <p className="font-mono bg-gray-100 p-1 rounded">{change.newValue}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AuditLogDetails;
